import axiosInstance from "@/api/axiosConfig";

import type {
  KeyFact,
  KeyFactActionBody,
  KeyFactActionResult,
  KeyFactDependent,
  KeyFactsCase,
  NoticeBriefPayload,
  NoticePreview,
  NoticeType
} from "./types";

/** The server waits 45 s for the read; past this the case finishes it. */
const PREVIEW_TIMEOUT_MS = 75_000;

const base = (individualId: string): string =>
  `/key-facts/${encodeURIComponent(individualId)}`;

const factPath = (individualId: string, key: string): string =>
  `${base(individualId)}/facts/${encodeURIComponent(key)}`;

export const KeyFactsAPI = {
  /** Read a notice before its case exists (the new-case wizard's prefill). */
  async previewNotice(file: File): Promise<NoticePreview> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await axiosInstance.post<NoticePreview>(
      "/notices/preview",
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: PREVIEW_TIMEOUT_MS
      }
    );
    return data;
  },

  async noticeBrief(
    individualId: string,
    noticeKey?: string
  ): Promise<NoticeBriefPayload> {
    const { data } = await axiosInstance.get<NoticeBriefPayload>(
      `${base(individualId)}/notice-brief`,
      { params: noticeKey ? { notice_key: noticeKey } : undefined }
    );
    return data;
  },

  /** Confirm What USCIS Asked: every in-force issue of the notice. */
  async confirmNotice(
    individualId: string,
    noticeKey: string
  ): Promise<KeyFactActionResult & { confirmed: string[] }> {
    const { data } = await axiosInstance.post<
      KeyFactActionResult & { confirmed: string[] }
    >(`${base(individualId)}/notices/confirm`, { notice_key: noticeKey });
    return data;
  },

  /** Confirm a proposed file as the notice (it is read), or dismiss it. */
  async resolveNoticeProposal(
    individualId: string,
    nodeId: string,
    accept: boolean,
    noticeType?: NoticeType
  ): Promise<void> {
    await axiosInstance.post(
      `${base(individualId)}/notice-proposals/${encodeURIComponent(nodeId)}`,
      { accept, notice_type: noticeType }
    );
  },

  async list(individualId: string): Promise<KeyFactsCase> {
    const { data } = await axiosInstance.get<KeyFactsCase>(base(individualId));
    return data;
  },

  /** One key fact plus what uses it (drafts, forms, other key facts, its memo). */
  async get(
    individualId: string,
    key: string
  ): Promise<KeyFact & { used_by: KeyFactDependent[] }> {
    const { data } = await axiosInstance.get<
      KeyFact & { used_by: KeyFactDependent[] }
    >(factPath(individualId, key));
    return data;
  },

  async act(
    individualId: string,
    key: string,
    body: KeyFactActionBody
  ): Promise<KeyFactActionResult> {
    const { data } = await axiosInstance.post<KeyFactActionResult>(
      `${factPath(individualId, key)}/action`,
      body
    );
    return data;
  },

  /** File the captured copy of a web source as a case file. */
  async fileSourceAsExhibit(
    individualId: string,
    key: string,
    source: string
  ): Promise<KeyFactActionResult & { node_id?: string }> {
    const { data } = await axiosInstance.post<
      KeyFactActionResult & { node_id?: string }
    >(`${factPath(individualId, key)}/sources/exhibit`, { source });
    return data;
  },

  async cancelAnalysis(individualId: string, key: string): Promise<void> {
    await axiosInstance.post(`${factPath(individualId, key)}/analysis/cancel`);
  },

  async refresh(individualId: string): Promise<void> {
    await axiosInstance.post(`${base(individualId)}/refresh`, {});
  }
};

/** The message of a Key Facts error (`next` is written for the agent, so it is left out). */
export const keyFactErrorText = (error: unknown): string => {
  const data = (error as { response?: { data?: { error?: string } } })?.response
    ?.data;
  return data?.error || (error as Error)?.message || "Something went wrong.";
};
